import { IAuthErrorMessage } from 'components/AuthError/AuthErrorMsg';
import EtoAuthError from 'components/AuthError/EtoAuthError';
import { SSOAPIContext } from 'helpers/context/ssoAPIContext';
import { useQuery } from 'helpers/converter/converter';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { SSOAPIContextParser } from 'services/api-context-service/sso-api-context-parser';
import { IEtoConfig } from '../../models/product/product';
import SiteList from './SiteList';

export const DEFAULT_SITE_ID_NONE_VALUE = -1;

const SiteListRenderer = (): React.ReactElement => {
    const query = useQuery();
    const [token] = useState(query.get('token') ?? '');
    const rawContext = useQuery().get('context') ?? '';
    const [error, setError] = useState<IAuthErrorMessage>({
        errorCode: '',
        jsonParams: {},
        errorDetails: {}
    });
    const history = useHistory();

    useEffect(() => {
        const url = new URL(window.location.href);
        const tokenPresent = url.searchParams.has('token');
        if (tokenPresent) {
            url.searchParams.delete('token');
            history.replace(url.pathname + url.search + url.hash);
        }
    }, [history]);

    const ssoAPIContext = useMemo(() => {
        try {
            return SSOAPIContextParser.getSSOAPIContextFromQueryParam(rawContext);
        } catch (error) {
            setError({
                errorCode: 'INVALID_QUERY_PARAM',
                jsonParams: { param: 'context' },
                errorDetails: {
                    react_error: (error as Error).message
                }
            });
            return new SSOAPIContext();
        }
    }, [setError, rawContext]);

    const config = useMemo(() => {
        let tempConfig: IEtoConfig = {};
        if (token === '') {
            setError({
                errorCode: 'MISSING_QUERY_PARAM',
                jsonParams: { param: 'token' },
                errorDetails: {...ssoAPIContext}
            });
            return tempConfig;
        }
        try {
            tempConfig = JSON.parse(atob(token.split('.')[1])) as Required<IEtoConfig>;
        } catch(error) {
            setError({
                errorCode: 'INVALID_QUERY_PARAM',
                jsonParams: { param: 'token' },
                errorDetails: {
                    react_error: (error as Error).message
                }
            });
            return tempConfig;
        }
        const {
            entGuid = '',
            authToken,
            sites,
            defaultSiteId,
        } = tempConfig;
        if (entGuid === '') {
            setError({
                errorCode: 'INVALID_QUERY_PARAM',
                jsonParams: { param: 'token' },
                errorDetails: {
                    react_error: 'empty entGuid claim'
                }
            });
            return tempConfig;
        }
        if (!authToken) {
            setError({
                errorCode: 'INVALID_QUERY_PARAM',
                jsonParams: { param: 'token' },
                errorDetails: {
                    react_error: 'empty authToken claim'
                }
            });
            return tempConfig;
        }

        if (defaultSiteId === DEFAULT_SITE_ID_NONE_VALUE && !(sites?.length)) {
            setError({
                errorCode: 'INVALID_QUERY_PARAM',
                jsonParams: { param: 'token' },
                errorDetails: {
                    react_error: 'no sites in token claim'
                }
            });
            return tempConfig;
        }

        return tempConfig;
    }, [token, setError, ssoAPIContext]);

    return (
        error.errorCode !== '' ?
            <EtoAuthError {...error} /> :
            <SiteList
                token={token}
                hasDefaultSiteId={!!(config.defaultSiteId && config.defaultSiteId !== DEFAULT_SITE_ID_NONE_VALUE)}
                entGuid={config.entGuid ?? ''}
                legalMessage={config.legalMessage}
                sites={config.sites}
                defaultSiteId={config.defaultSiteId}
                ssoAPIContext={ssoAPIContext}
                onFatalError={(e: IAuthErrorMessage) => setError({...e})}
            />
    );
};
export default SiteListRenderer;
